import { useState, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CloseButton,
  Container,
  VStack,
  HStack,
  Stack,
  Box,
  Heading,
  Text,
  Button,
  Spinner,
  Alert,
  Switch,
  SimpleGrid,
  useDisclosure,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiTrash2 } from 'react-icons/fi'
import { TurfMap, TurfLegendPanel, OverlayModeToggle } from '../components/turf'
import { ResponsiveModal, LightSurface, BackLink } from '../components/shared'
import { useReturnTo } from '../lib/returnNav'
import { TurfMapStatsRow } from '../components/turfMapOverview/TurfMapStatsRow'
import { TurfMapTurfTable } from '../components/turfMapOverview/TurfMapTurfTable'
import { sumEstimatedMinutes } from '../components/turfMapOverview/turfEstimate'
import { ContactDetailModal } from '../components/contactDetail'
import { BusinessDetailModal } from '../components/business/BusinessDetailModal'
import { useTurfMapDetail, useDeleteTurfMap, useTurfMapHouseholds, useTurfMapContactsWithEmployment } from '../hooks/useTurfMap'
import { useExportTurfToList, useOptimizeWalkOrder } from '../hooks/useTurf'
import { groupContactsByWorkplace, splitByEmploymentStatus, contactsToHouseholdMarkers } from '../components/turf/workplaceUtils'
import type { TurfDto } from '../types/turf'
import type { HouseholdMarker, TurfBoundary } from '../types/turfMap'
import type { OverlayMode } from '../types/workplaceOverlay'
import { TURF_COLORS } from '../lib/mapConfig'

export function TurfMapOverviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const { data: turfMap, isLoading, error } = useTurfMapDetail(id)
  const { data: households } = useTurfMapHouseholds(id)
  const deleteTurfMapMutation = useDeleteTurfMap()
  const exportTurfMutation = useExportTurfToList()
  const optimizeTurfMutation = useOptimizeWalkOrder()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const turfDetailModal = useDisclosure()
  const [selectedTurf, setSelectedTurf] = useState<TurfDto | null>(null)

  // Overlay mode state
  const [overlayMode, setOverlayMode] = useState<OverlayMode>('home')
  const [hideVisited, setHideVisited] = useState(false)

  // Contact/Worksite detail modal state
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [selectedWorksiteId, setSelectedWorksiteId] = useState<string | null>(null)
  const [isWorksiteModalOpen, setIsWorksiteModalOpen] = useState(false)

  // Fetch employment data when overlay mode is work or hybrid
  const { data: contactsWithEmployment } = useTurfMapContactsWithEmployment(
    turfMap?.id,
    overlayMode !== 'home'
  )

  // Compute workplace markers and hybrid household markers
  const { workplaceMarkers, hybridHouseholds } = useMemo(() => {
    if (!contactsWithEmployment || overlayMode === 'home') {
      return { workplaceMarkers: undefined, hybridHouseholds: undefined }
    }

    if (overlayMode === 'work') {
      return {
        workplaceMarkers: groupContactsByWorkplace(contactsWithEmployment),
        hybridHouseholds: undefined,
      }
    }

    // Hybrid mode
    const { withWork, withoutWork } = splitByEmploymentStatus(contactsWithEmployment)
    return {
      workplaceMarkers: groupContactsByWorkplace(withWork),
      hybridHouseholds: contactsToHouseholdMarkers(withoutWork),
    }
  }, [contactsWithEmployment, overlayMode])

  // Memoize turf boundaries for map display
  const turfBoundaries: TurfBoundary[] = useMemo(() => {
    const results: TurfBoundary[] = []
    for (const [idx, t] of (turfMap?.turfs ?? []).entries()) {
      if (!t.boundaryGeoJson) continue
      try {
        const geometry = JSON.parse(t.boundaryGeoJson)
        results.push({
          id: t.id,
          label: t.label,
          color: TURF_COLORS[idx % TURF_COLORS.length],
          geoJson: {
            type: 'Feature' as const,
            geometry,
            properties: {},
          },
        })
      } catch {
        // skip invalid geometry
      }
    }
    return results
  }, [turfMap?.turfs])

  // Memoize household markers (with optional hide-visited filter)
  const householdMarkers: HouseholdMarker[] = useMemo(() => {
    const all = households?.map((h) => ({
      householdKey: h.householdKey,
      longitude: h.longitude,
      latitude: h.latitude,
      streetAddress: h.streetAddress,
      city: h.city,
      state: h.state,
      zip: h.zip,
      contactCount: h.contacts.length,
      visitedCount: h.contacts.filter(c => c.isVisited).length,
      needsRevisitCount: h.contacts.filter(c => c.isVisited && (c.contactStatus === 4 || c.contactStatus === 5)).length,
      contacts: h.contacts.map((c) => ({
        contactId: c.contactId,
        contactName: c.contactName,
        walkOrder: c.walkOrder,
        isVisited: c.isVisited,
        contactStatus: c.contactStatus,
        turfId: c.turfId,
        turfIndex: c.turfIndex,
      })),
    })) ?? []

    if (hideVisited) {
      return all.filter(h => h.visitedCount < h.contactCount || h.needsRevisitCount > 0)
    }
    return all
  }, [households, hideVisited])

  // Pre-compute household counts per turf (avoids O(n*m) in table + legend renders)
  const turfHouseholdCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = {}
    if (!households) return counts
    for (const h of households) {
      const seen = new Set<string>()
      for (const c of h.contacts) {
        if (c.turfId && !seen.has(c.turfId)) {
          seen.add(c.turfId)
          counts[c.turfId] = (counts[c.turfId] ?? 0) + 1
        }
      }
    }
    return counts
  }, [households])

  const backTarget = turfMap?.campaignId
    ? `/dashboard/campaigns/${turfMap.campaignId}`
    : '/dashboard/campaigns'
  const back = useReturnTo({
    to: backTarget,
    label: turfMap?.campaignId ? 'Back to Campaign' : 'Back to Campaigns',
  })

  const handleDeleteTurfMap = async () => {
    if (!id) return
    const campaignId = turfMap?.campaignId
    try {
      await deleteTurfMapMutation.mutateAsync(id)
      toaster.create({
        title: 'Turf Map deleted',
        type: 'success',
        duration: 4000,
      })
      navigate(campaignId ? `/dashboard/campaigns/${campaignId}` : '/dashboard/campaigns')
    } catch {
      toaster.create({
        title: 'Delete failed',
        description: 'An error occurred while deleting the turf map.',
        type: 'error',
        duration: 5000,
      })
    } finally {
      setShowDeleteDialog(false)
    }
  }

  const handleTurfRowClick = (turf: TurfDto) => {
    setSelectedTurf(turf)
    turfDetailModal.onOpen()
  }

  const handleOptimizeTurf = async (turfId: string) => {
    try {
      await optimizeTurfMutation.mutateAsync(turfId)
      toaster.create({
        title: 'Walk order optimized',
        description: 'Contact visit order has been optimized using nearest-neighbor routing.',
        type: 'success',
        duration: 4000,
      })
    } catch {
      toaster.create({
        title: 'Optimization failed',
        description: 'An error occurred while optimizing the walk order.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handleExportTurf = async (turfId: string, label: string) => {
    try {
      const result = await exportTurfMutation.mutateAsync({
        id: turfId,
        data: {
          listLabel: `${label} — Export`,
          listDescription: 'Exported from turf',
        },
      })
      toaster.create({
        title: 'Exported to list',
        description: 'Turf members exported as a new contact list.',
        type: 'success',
        duration: 4000,
      })
      navigate(`/dashboard/lists/${result.listId}/edit`)
    } catch {
      toaster.create({
        title: 'Export failed',
        description: 'An error occurred while exporting the turf to a list.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handleContactClick = (contactId: string) => {
    setSelectedContactId(contactId)
    setIsContactModalOpen(true)
  }

  const handleWorksiteClick = (businessId: string) => {
    setSelectedWorksiteId(businessId)
    setIsWorksiteModalOpen(true)
  }

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="gray.500">Loading turf map...</Text>
        </VStack>
      </Container>
    )
  }

  if (!!error || !turfMap) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          Failed to load turf map. It may not exist or you may not have access.
        </Alert.Root>
        <Button mt={4} onClick={() => navigate('/dashboard/campaigns')}>
          Back to Campaigns
        </Button>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8} px={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        {/* Back button */}
        {back && (
          <HStack>
            <BackLink to={back.to} label={back.label} />
          </HStack>
        )}

        {/* Header + Actions */}
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'start' }} gap={4}>
          <Box>
            <HStack gap={3}>
              <Heading size="lg">{turfMap.label}</Heading>
            </HStack>
            {turfMap.sourceContactListLabel && (
              <Text color="gray.500" mt={1}>
                Source: {turfMap.sourceContactListLabel}
              </Text>
            )}
          </Box>
          <Stack direction={{ base: 'column', md: 'row' }} gap={2}>
            <Button
              size="sm"
              colorPalette="red"
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}><FiTrash2 />Delete
                          </Button>
          </Stack>
        </Stack>

        {/* Stats Row */}
        <TurfMapStatsRow
          totalContactCount={turfMap.totalContactCount}
          totalHouseholdCount={turfMap.totalHouseholdCount}
          turfCount={turfMap.turfs.length}
          totalVisitedCount={turfMap.totalVisitedCount}
          estimatedMinutes={sumEstimatedMinutes(turfMap.turfs)}
        />

        {/* Map */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md" position="relative">
          <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} mb={3} gap={3}>
            <Text fontWeight="medium">Map</Text>
            <HStack gap={4}>
              <Field.Root display="flex" alignItems="center" w="auto">
                <Field.Label htmlFor="hide-visited" mb={0} fontSize="sm" color="gray.600">
                  Hide visited
                </Field.Label>
                <Switch.Root
                  id="hide-visited"
                  size="sm"
                  checked={hideVisited}
                  onCheckedChange={(e) => setHideVisited(e.checked === true)}
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </Field.Root>
              <OverlayModeToggle mode={overlayMode} onChange={setOverlayMode} />
            </HStack>
          </Stack>
          <LightSurface>
            <Box position="relative" mx={{ base: -4, md: 0 }}>
              <TurfMap
                boundaries={turfBoundaries}
                households={
                  overlayMode === 'work' ? [] :
                  overlayMode === 'hybrid' ? (hybridHouseholds ?? []) :
                  householdMarkers
                }
                workplaces={overlayMode !== 'home' ? workplaceMarkers : undefined}
                onHouseholdContactClick={handleContactClick}
                onWorkplaceContactClick={handleContactClick}
                onWorksiteClick={handleWorksiteClick}
                height="550px"
              />
              <TurfLegendPanel
                turfs={turfMap.turfs.map((t, idx) => ({
                  id: t.id,
                  index: idx + 1,
                  label: t.label,
                  color: TURF_COLORS[idx % TURF_COLORS.length],
                  contactCount: t.memberCount,
                  householdCount: turfHouseholdCounts[t.id] ?? 0,
                }))}
                readonly
                showHouseholdStatusKey={overlayMode !== 'work'}
              />
            </Box>
          </LightSurface>
        </Box>

        {/* Turf Table */}
        <TurfMapTurfTable
          turfs={turfMap.turfs}
          turfHouseholdCounts={turfHouseholdCounts}
          onTurfClick={handleTurfRowClick}
          onOptimize={handleOptimizeTurf}
          onExport={handleExportTurf}
          isOptimizing={optimizeTurfMutation.isPending}
          isExporting={exportTurfMutation.isPending}
        />
      </VStack>

      {/* Delete TurfMap Dialog */}
      <Dialog.Root
        open={showDeleteDialog}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setShowDeleteDialog(false);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Delete Turf Map
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>"{turfMap.label}"</strong>? This will remove
                  all associated turfs and member assignments. This action cannot be undone.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setShowDeleteDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDeleteTurfMap}
                    ml={3}
                    loading={deleteTurfMapMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Turf Detail Modal */}
      <ResponsiveModal
        isOpen={turfDetailModal.open}
        onClose={turfDetailModal.onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Text>{selectedTurf?.label}</Text>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              {selectedTurf && (
                <VStack gap={4} align="stretch">
                  <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Members
                      </Text>
                      <Text fontWeight="bold">{selectedTurf.memberCount}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Visited
                      </Text>
                      <Text fontWeight="bold">{selectedTurf.visitedCount}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Progress
                      </Text>
                      <Text fontWeight="bold">
                        {selectedTurf.memberCount > 0
                          ? Math.round((selectedTurf.visitedCount / selectedTurf.memberCount) * 100)
                          : 0}
                        %
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Est. Time
                      </Text>
                      <Text fontWeight="bold">
                        {selectedTurf.estimatedMinutes ? `${selectedTurf.estimatedMinutes} min` : '-'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                  <Text fontSize="sm" color="gray.600">
                    Click "View Details" to see full turf information and member list.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={turfDetailModal.onClose}>
                Close
              </Button>
              <Button
                colorPalette="brand"
                onClick={() => {
                  if (selectedTurf) {
                    navigate(`/dashboard/turfs/${selectedTurf.id}`)
                  }
                }}
              >
                View Details
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      {/* Contact Detail Modal */}
      {selectedContactId && (
        <ContactDetailModal
          contactId={selectedContactId}
          isOpen={isContactModalOpen}
          onClose={() => {
            setIsContactModalOpen(false)
            setSelectedContactId(null)
          }}
        />
      )}

      {/* Business Detail Modal */}
      {selectedWorksiteId && (
        <BusinessDetailModal
          worksiteId={selectedWorksiteId}
          isOpen={isWorksiteModalOpen}
          onClose={() => {
            setIsWorksiteModalOpen(false)
            setSelectedWorksiteId(null)
          }}
          onEmployeeClick={handleContactClick}
        />
      )}
    </Container>
  );
}
